"use client";

import { useState } from "react";
import { Check, Copy } from "lucide-react";

import type { DemoFinetune } from "@/lib/demo-finetunes";
import PageContent from "@/components/page-content";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SectionHeading } from "./SectionHeading";

interface SampleCodeProps {
  finetune: DemoFinetune;
}

function toPythonString(value: string) {
  return JSON.stringify(value);
}

function buildSftSnippet(finetune: DemoFinetune) {
  return `import moondream as md

lens = md.Lens()

# Train on labeled examples for this task.
lens.train_step(
    training_data=[
        {
            "image": image,
            "prompt": ${toPythonString(finetune.codePrompt)},
            "output": ${toPythonString(finetune.codeSftOutput)},
        }
        for image in training_images
    ]
)`;
}

function buildRlSnippet(finetune: DemoFinetune) {
  return `import moondream as md

lens = md.Lens()

# 1. Generate rollouts for your prompt.
rollouts = lens.create_rollouts(
    images=my_images,
    prompt=${toPythonString(finetune.codePrompt)},
)

# 2. Score each model output.
for rollout in rollouts:
    rollout.score = my_scoring_function(rollout.output)

# 3. Train on the scored rollouts.
lens.train_step(rollouts=rollouts)

# 4. Repeat, then evaluate.`;
}

export function SampleCode({ finetune }: SampleCodeProps) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null);
  const snippets = {
    sft: buildSftSnippet(finetune),
    rl: buildRlSnippet(finetune),
  };

  const copyToClipboard = async (tabId: keyof typeof snippets) => {
    try {
      await navigator.clipboard.writeText(snippets[tabId]);
      setCopiedTab(tabId);
      window.setTimeout(() => setCopiedTab(null), 2000);
    } catch (error) {
      console.error("Failed to copy snippet", error);
    }
  };

  return (
    <PageContent>
      <section className="space-y-10">
        <SectionHeading
          eyebrow="Code"
          title="See the code"
          description="These examples show the minimum Python you need to run this workflow with SFT or RL."
        />

        <Card className="border-border/60 bg-card/80 shadow-sm">
          <CardContent className="p-6">
            <Tabs defaultValue="sft" className="w-full">
              <TabsList className="grid h-auto w-full grid-cols-2">
                <TabsTrigger value="sft" className="py-2">
                  SFT example
                </TabsTrigger>
                <TabsTrigger value="rl" className="py-2">
                  RL example
                </TabsTrigger>
              </TabsList>

              {(["sft", "rl"] as const).map((tabId) => (
                <TabsContent key={tabId} value={tabId} className="mt-4">
                  <div className="relative">
                    <pre className="overflow-x-auto rounded-2xl border border-border/60 bg-background/70 p-5 text-sm leading-7 text-foreground">
                      <code>{snippets[tabId]}</code>
                    </pre>

                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="absolute right-3 top-3 h-8 w-8"
                      onClick={() => copyToClipboard(tabId)}
                      aria-label={`Copy ${tabId.toUpperCase()} example`}
                    >
                      {copiedTab === tabId ? (
                        <Check className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </section>
    </PageContent>
  );
}
